import React, { useRef, useState } from 'react';
import { api, fileUrl } from '@/lib/api';
import { Upload, X, Film, AlertCircle, RotateCcw } from 'lucide-react';

function formatMB(bytes) {
  if (!bytes && bytes !== 0) return '0 MB';
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export default function VideoUploader({ value, onChange, label = 'Video' }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [info, setInfo] = useState(null);
  const [progress, setProgress] = useState({ loaded: 0, total: 0, pct: 0 });
  const [lastFile, setLastFile] = useState(null);

  const upload = async (file) => {
    if (!file) return;
    setLastFile(file);
    setErr('');
    setInfo(null);
    setProgress({ loaded: 0, total: file.size || 0, pct: 0 });
    setBusy(true);
    try {
      const form = new FormData();
      form.append('file', file);
      const r = await api.post('/admin/uploads/video', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (evt) => {
          const total = evt.total || file.size || 0;
          const loaded = evt.loaded || 0;
          const pct = total > 0 ? Math.min(100, Math.round((loaded / total) * 100)) : 0;
          setProgress({ loaded, total, pct });
        },
      });
      onChange(r.data.url);
      setInfo({ size: r.data.size, filename: r.data.filename });
      setProgress({ loaded: 0, total: 0, pct: 0 });
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || 'Upload failed');
    } finally {
      setBusy(false);
    }
  };

  const retry = () => {
    if (lastFile) upload(lastFile);
  };

  return (
    <div data-testid="video-uploader">
      {label && (
        <label className="block font-body text-[12px] font-semibold uppercase text-ink-muted tracking-wider2 mb-2">
          {label}
        </label>
      )}
      <div className="flex items-start gap-4 flex-wrap">
        <div className="w-56 h-32 border border-border-line rounded-sm bg-card-dark overflow-hidden flex items-center justify-center text-white/60">
          {value ? (
            <video
              src={fileUrl(value)}
              className="w-full h-full object-cover"
              muted
              loop
              playsInline
              autoPlay
            />
          ) : (
            <div className="flex flex-col items-center gap-1 text-[10px] uppercase tracking-wider3">
              <Film size={20} />
              <span>No video</span>
            </div>
          )}
        </div>

        <div className="flex flex-col gap-2 flex-1 min-w-[220px]">
          <input
            ref={inputRef}
            type="file"
            accept="video/mp4"
            className="hidden"
            onChange={(e) => upload(e.target.files?.[0])}
            data-testid="video-upload-input"
          />
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            className="btn-secondary !py-2 !px-3 text-[11px] self-start"
            disabled={busy}
            data-testid="video-upload-button"
          >
            <Upload size={14} className="mr-1" /> {busy ? 'Uploading…' : 'Upload MP4 (max 500MB)'}
          </button>

          {busy && (
            <div className="w-full max-w-sm" data-testid="upload-progress">
              <div className="flex items-center justify-between mb-1">
                <span className="font-body font-semibold text-[11px] uppercase tracking-wider2 text-ink-primary" data-testid="upload-progress-pct">
                  {progress.pct}%
                </span>
                <span className="font-body text-[10px] text-ink-soft" data-testid="upload-progress-size">
                  {formatMB(progress.loaded)} / {formatMB(progress.total)}
                </span>
              </div>
              <div className="w-full h-1.5 rounded-full bg-border-line overflow-hidden" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={progress.pct}>
                <div
                  className="h-full bg-brand-orange transition-[width] duration-200 ease-out"
                  style={{ width: `${progress.pct}%` }}
                  data-testid="upload-progress-bar"
                />
              </div>
            </div>
          )}

          {value && !busy && (
            <button
              type="button"
              onClick={() => { onChange(''); setInfo(null); }}
              className="btn-secondary !py-2 !px-3 text-[11px] self-start"
              data-testid="video-remove-button"
            >
              <X size={14} className="mr-1" /> Remove
            </button>
          )}

          {info && !busy && (
            <div className="font-body text-[11px] text-ink-soft" data-testid="upload-info">
              Uploaded · {formatMB(info.size)}
            </div>
          )}

          {err && !busy && (
            <div className="flex flex-col gap-2" data-testid="upload-error">
              <div className="flex items-start gap-1.5 font-body text-[11px] text-brand-red">
                <AlertCircle size={14} className="shrink-0 mt-0.5" />
                <span>{err}</span>
              </div>
              {lastFile && (
                <button
                  type="button"
                  onClick={retry}
                  className="btn-secondary !py-2 !px-3 text-[11px] self-start"
                  data-testid="upload-retry-button"
                >
                  <RotateCcw size={14} className="mr-1" /> Try again
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
